// src/stores/onboardingDraft.ts
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { Preferences } from '@capacitor/preferences'
import type { OnboardingDraft, OnboardingDraftChild, OnboardingDraftTask, OnboardingDraftReward } from '@/types/onboarding'

const STORAGE_KEY = 'harmonia_onboarding_draft'
const DRAFT_VERSION = 1

function uuid(): string {
  // crypto.randomUUID dispo en WKWebView/Chromium modernes ; fallback simple sinon.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function emptyDraft(): OnboardingDraft {
  return {
    version: DRAFT_VERSION,
    parentName: '',
    parentType: null,
    declaredChildrenCount: null,
    locale: 'fr',
    children: [],
    tasks: [],
    rewards: [],
    createdAt: new Date().toISOString(),
    commitId: null,
    committedAt: null,
  }
}

export const useOnboardingDraftStore = defineStore('onboardingDraft', () => {
  const draft = ref<OnboardingDraft | null>(null)

  const isEmpty = computed(() => !draft.value || (draft.value.children.length === 0 && draft.value.parentName === ''))

  async function persist(): Promise<void> {
    if (draft.value) await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(draft.value) })
    else await Preferences.remove({ key: STORAGE_KEY })
  }

  // Persistance réactive (débouncée au tick suivant via watch).
  watch(draft, () => { void persist() }, { deep: true })

  function ensureDraft(): OnboardingDraft {
    if (!draft.value) draft.value = emptyDraft()
    return draft.value
  }

  async function load(): Promise<void> {
    const { value } = await Preferences.get({ key: STORAGE_KEY })
    if (!value) { draft.value = null; return }
    try { draft.value = JSON.parse(value) as OnboardingDraft }
    catch { await Preferences.remove({ key: STORAGE_KEY }); draft.value = null }
  }

  function setParent(name: string, locale: string, parentType?: string | null): void {
    const d = ensureDraft()
    d.parentName = name
    d.locale = locale
    // `undefined` veut dire « l'appelant ne se prononce pas » et laisse la
    // valeur en place ; seul un null explicite l'efface.
    if (parentType !== undefined) d.parentType = parentType
  }

  function addChild(child: OnboardingDraftChild): void { ensureDraft().children.push(child) }
  function setChildren(children: OnboardingDraftChild[]): void { ensureDraft().children = children }
  function setDeclaredChildrenCount(count: number | null): void { ensureDraft().declaredChildrenCount = count }
  function setTasks(tasks: OnboardingDraftTask[]): void { ensureDraft().tasks = tasks }
  function setRewards(rewards: OnboardingDraftReward[]): void { ensureDraft().rewards = rewards }

  function ensureCommitId(): string {
    const d = ensureDraft()
    if (!d.commitId) d.commitId = uuid()
    return d.commitId
  }

  function markCommitted(): void {
    if (draft.value) draft.value.committedAt = new Date().toISOString()
  }

  // Instantané des compteurs au moment du clear() (le commit purge le brouillon
  // AVANT que finish() n'émette onboarding_completed — sans ce relais, les
  // compteurs de l'événement terminal seraient à zéro en mode create).
  const lastClearedCounts = ref<{ children: number; tasks: number; rewards: number; declaredChildrenCount: number | null } | null>(null)

  async function clear(): Promise<void> {
    if (draft.value) {
      lastClearedCounts.value = {
        children: draft.value.children.length,
        tasks: draft.value.tasks.length,
        rewards: draft.value.rewards.length,
        declaredChildrenCount: draft.value.declaredChildrenCount ?? null,
      }
    }
    draft.value = null
    await Preferences.remove({ key: STORAGE_KEY })
  }

  return { draft, isEmpty, lastClearedCounts, load, setParent, addChild, setChildren, setDeclaredChildrenCount, setTasks, setRewards, ensureCommitId, markCommitted, clear }
})
